import { useParams } from 'react-router'
import styled from 'styled-components'
import ProductTeaser from '../../../components/ProductTeaser/ProductTeaser'
import { Col, Row } from '../../../components/Ui-Kits/Grid'
import { useAppTranslation } from '../../../i18n/hooks'
import { useQuery } from '../../../hooks/query/useQuery'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { intersection } from 'lodash'
import lunr from 'lunr'
import { any, bool } from 'prop-types'
import { useFilteredData } from './useFilteredData'
import { TextStyle } from 'components/Ui-Kits/Text'
import { useSetQuery } from 'hooks/query/useSetQuery'
import { useGetTranslated } from 'hooks/useGetTranslated'
import { Pagination } from 'components/Pagination'
import { isEqual } from 'lodash'

export const ProductList = ({ filters, auctionHasEnded, products }) => {
  const { auctionId } = useParams()

  const { t } = useAppTranslation()
  const rowRef = useRef(null)
  const getTranslated = useGetTranslated()

  const productsIndex = useMemo(() => {
    const index = lunr(builder => {
      builder.field('name')
      builder.field('description')

      products.forEach(({ name, description, ...others }) => {
        builder.add({
          name: getTranslated(name),
          description: getTranslated(description) || '',
          ...others,
        })
      })
    })

    return index
  }, [products, getTranslated])

  const { materials, category, conditions, rulers, references, tags, countries, q = '', page = 1 } = useQuery()

  const filteredProducts = useMemo(
    () =>
      products
        .filter(({ id }) => productsIndex.search(q).find(({ ref }) => ref === id))
        .filter(
          ({ technicalDetails, tags: pTags, categories: productCategories }) =>
            (!materials?.length || materials.includes(technicalDetails?.material)) &&
            (!category || category == technicalDetails?.auctionCategory?.id || productCategories?.includes(category)) &&
            (!tags?.length || !!intersection(tags, pTags?.map(({ id }) => id) || []).length) &&
            (!conditions?.length || conditions.includes(technicalDetails?.condition)) &&
            (!countries?.length || countries.includes(technicalDetails?.country)) &&
            (!rulers?.length || rulers.includes(technicalDetails?.ruler)) &&
            (!references?.length || references.includes(technicalDetails?.reference))
        ),
    [materials, category, conditions, rulers, references, tags, products, productsIndex, q, countries]
  )
  const setQuery = useSetQuery()

  const setPage = useCallback(
    page => {
      setQuery({ page })
    },
    [setQuery]
  )

  const initialPage = useRef(page)

  const queryRef = useRef({ materials, category, conditions, rulers, references, tags, q })
  useEffect(() => {
    if (isEqual(queryRef.current, { materials, category, conditions, rulers, references, tags, q })) return

    setPage(initialPage.current)
    initialPage.current = 1
    queryRef.current = { materials, category, conditions, rulers, references, tags, q }
  }, [materials, category, conditions, rulers, references, tags, q, setPage])

  const paginatedProducts = useMemo(
    () => [...filteredProducts].slice((page - 1) * 12, page * 12),
    [filteredProducts, page]
  )

  const { filteredData, clearFilterItem } = useFilteredData(filters)

  return (
    <Wrap ref={rowRef}>
      <Row>
        {!!filteredData.length && (
          <Col col={12} mt={0} mb={0}>
            <FilterDataButton onClick={() => clearFilterItem()}>{t('clearAll')}</FilterDataButton>
            {filteredData.map(({ label, key, id }) => (
              <FilterDataButton key={id} onClick={() => clearFilterItem(key, id)}>
                {label} &times;
              </FilterDataButton>
            ))}
          </Col>
        )}
      </Row>
      <Row>
        {paginatedProducts?.map(product => (
          <Col sm={12} md={6} col={3} key={product.id}>
            <ProductTeaser
              id={!auctionHasEnded ? product.id : undefined}
              name={product.sort + 1 + '. ' + getTranslated(product?.name)}
              category={getTranslated(product?.category?.label)}
              image={product?.featuredImage?.url}
              price={product.secondHighestBid || product?.technicalDetails?.AuctionMinimumPrice}
              finalPrice={product.auctionStatus === 'sold' ? product.secondHighestBid : undefined}
              startingPrice={product?.technicalDetails?.AuctionMinimumPrice}
              path={`/auction/${auctionId}/${product.id}`}
              bidsCount={product.bidsCount}
              status={auctionHasEnded ? product.auctionStatus : undefined}
              tags={product.tags}
              auctionStatus={product.auctionStatus}
              auctionId={auctionId}
              badgeLabel={
                !auctionHasEnded && product.bidsCount ? t('offers', { bidsCount: product.bidsCount }) : undefined
              }
              noPadding
            />
          </Col>
        ))}
      </Row>
      <Pagination
        total={filteredProducts.length}
        perPage={12}
        currentPage={+page}
        onPageChange={page => {
          setPage(page)
          window.scrollTo({
            top: rowRef.current.getBoundingClientRect().top + window.scrollY - 100,
            behavior: 'smooth',
          })
        }}
      />
    </Wrap>
  )
}

ProductList.propTypes = {
  filters: any,
  auctionHasEnded: bool,
  products: any,
}

const Wrap = styled.div`
  flex: 1;
`

const FilterDataButton = styled.button`
  ${TextStyle}
  background-color: transparent;
  border: 0;
  margin-right: 0.5rem;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`
